import { apiService } from './api';
import type {
  ImportedNodeType,
  ImportedNodesResponse,
  ImportStatusResponse,
  ImportFromNpmResponse,
} from '../types/nodeImport.types';

class NodeImportService {
  private baseUrl = '/node-import';

  async getImportedNodes(params?: {
    page?: number;
    page_size?: number;
    search?: string;
    category?: string;
    is_active?: boolean;
    has_errors?: boolean;
  }): Promise<ImportedNodesResponse> {
    return await apiService.get<ImportedNodesResponse>(`${this.baseUrl}/imported-nodes/`, params);
  }

  async getActiveNodes(): Promise<{ count: number; nodes: ImportedNodeType[] }> {
    return await apiService.get<{ count: number; nodes: ImportedNodeType[] }>(`${this.baseUrl}/imported-nodes/active/`);
  }

  async activateNode(id: string): Promise<{ message: string; is_active: boolean }> {
    return await apiService.post<{ message: string; is_active: boolean }>(`${this.baseUrl}/imported-nodes/${id}/activate/`);
  }

  async deactivateNode(id: string): Promise<{ message: string; is_active: boolean }> {
    return await apiService.post<{ message: string; is_active: boolean }>(`${this.baseUrl}/imported-nodes/${id}/deactivate/`);
  }

  async deleteNode(id: string): Promise<void> {
    await apiService.delete(`${this.baseUrl}/imported-nodes/${id}/`);
  }

  async deleteAllImportedNodes(params?: {
    search?: string;
    category?: string;
    is_active?: boolean;
    has_errors?: boolean;
  }): Promise<{ deleted: number }> {
    // Bulk delete supports the same filters as getImportedNodes via query params.
    const usp = new URLSearchParams();
    if (params?.search) usp.set('search', params.search);
    if (params?.category) usp.set('category', params.category);
    if (params?.is_active !== undefined) usp.set('is_active', params.is_active ? 'true' : 'false');
    if (params?.has_errors !== undefined) usp.set('has_errors', params.has_errors ? 'true' : 'false');

    const qs = usp.toString();
    const url = `${this.baseUrl}/imported-nodes/delete-all/${qs ? `?${qs}` : ''}`;
    return await apiService.post<{ deleted: number }>(url, { confirm: true });
  }

  async importFromNpm(packageName: string): Promise<ImportFromNpmResponse> {
    // npm installs / parsing large n8n packages can take well over 30s, so
    // override the global default for this single call.
    return await apiService.post<ImportFromNpmResponse>(
      `${this.baseUrl}/import-jobs/import-from-npm/`,
      {
        node_package_name: packageName,
        node_package_path: packageName,
      },
      { timeout: 5 * 60 * 1000 }
    );
  }

  async getStatus(): Promise<ImportStatusResponse> {
    return await apiService.get<ImportStatusResponse>(`${this.baseUrl}/system/utils/status/`);
  }
}

export const nodeImportService = new NodeImportService();


